// Comprehensive Localization System with Human-Friendly Texts
// Supports: English (en) and Arabic (ar)

export type SupportedLanguage = 'en' | 'ar';

export interface Translations {
    // App General
    appName: string;
    tagline: string;
    welcome: string;
    loading: string;
    error: string;
    success: string;
    cancel: string;
    confirm: string;
    save: string;
    delete: string;
    edit: string;
    close: string;
    back: string;
    next: string;
    done: string;
    search: string;
    filter: string;
    refresh: string;
    settings: string;
    help: string;
    logout: string;

    // Navigation
    nav: {
        home: string;
        map: string;
        contacts: string;
        alerts: string;
        profile: string;
        guardian: string;
    };

    // Auth
    auth: {
        login: string;
        register: string;
        username: string;
        password: string;
        confirmPassword: string;
        fullName: string;
        enterName: string;
        chooseAvatar: string;
        selectAvatar: string;
        createAccount: string;
        accessSystem: string;
        forgotPassword: string;
        alreadyHaveAccount: string;
        dontHaveAccount: string;
        registerHere: string;
        loginHere: string;
        authenticating: string;
        accessGranted: string;
        initializingSystem: string;
    };

    // Dashboard
    dashboard: {
        title: string;
        greeting: string;
        yourLocation: string;
        safetyStatus: string;
        weatherTitle: string;
        newsTitle: string;
        localNews: string;
        cityNews: string;
        noAlerts: string;
        youAreSafe: string;
        lowRisk: string;
        moderateRisk: string;
        highRisk: string;
        wind: string;
        humidity: string;
        temperature: string;
        updatedAgo: string;
        viewOnMap: string;
        newsItems: string;
    };

    // Contacts
    contacts: {
        title: string;
        network: string;
        addContact: string;
        searchContacts: string;
        noContacts: string;
        addFirstContact: string;
        connected: string;
        online: string;
        offline: string;
        lastSeen: string;
        sendMessage: string;
        viewLocation: string;
        checkStatus: string;
        shareLink: string;
        scanQR: string;
        enterID: string;
        nearby: string;
        relationship: string;
        family: string;
        friend: string;
        colleague: string;
        emergency: string;
        requestSent: string;
        waitingResponse: string;
    };

    // Safety Status
    status: {
        safe: string;
        home: string;
        warning: string;
        danger: string;
        sos: string;
        unknown: string;
    };

    // Guardian AI
    guardian: {
        title: string;
        subtitle: string;
        askQuestion: string;
        thinking: string;
        voiceMode: string;
        textMode: string;
        safetyTips: string;
        emergencyAdvice: string;
    };

    // Alerts
    alerts: {
        title: string;
        activeAlerts: string;
        noActiveAlerts: string;
        reportHazard: string;
        verifyReport: string;
        viewDetails: string;
        distance: string;
        reportedBy: string;
        verified: string;
        unverified: string;
    };

    // Emergency
    emergency: {
        title: string;
        callPolice: string;
        callAmbulance: string;
        callFire: string;
        iAmSafe: string;
        needHelp: string;
        sosActivated: string;
        shareLocation: string;
    };

    // Profile
    profile: {
        title: string;
        myProfile: string;
        editProfile: string;
        changeAvatar: string;
        locationSharing: string;
        notifications: string;
        language: string;
        about: string;
        version: string;
    };

    // Time
    time: {
        justNow: string;
        minutesAgo: string;
        hoursAgo: string;
        today: string;
        yesterday: string;
    };
}

export const LOCALES: Record<SupportedLanguage, Translations> = {
    en: {
        // App General
        appName: 'Protectly',
        tagline: 'Your Family Safety Guardian',
        welcome: 'Welcome',
        loading: 'Loading...',
        error: 'Something went wrong',
        success: 'Success!',
        cancel: 'Cancel',
        confirm: 'Confirm',
        save: 'Save',
        delete: 'Delete',
        edit: 'Edit',
        close: 'Close',
        back: 'Back',
        next: 'Next',
        done: 'Done',
        search: 'Search',
        filter: 'Filter',
        refresh: 'Refresh',
        settings: 'Settings',
        help: 'Help',
        logout: 'Logout',

        // Navigation
        nav: {
            home: 'Home',
            map: 'Map',
            contacts: 'Contacts',
            alerts: 'Alerts',
            profile: 'Profile',
            guardian: 'Guardian',
        },

        // Auth
        auth: {
            login: 'Login',
            register: 'Register',
            username: 'Username',
            password: 'Password',
            confirmPassword: 'Confirm Password',
            fullName: 'Full Name',
            enterName: 'Enter your full name',
            chooseAvatar: 'Choose Your Avatar',
            selectAvatar: 'Select an avatar that represents you',
            createAccount: 'Create Account',
            accessSystem: 'Access System',
            forgotPassword: 'Forgot password?',
            alreadyHaveAccount: 'Already have an account?',
            dontHaveAccount: "Don't have an account?",
            registerHere: 'Register here',
            loginHere: 'Login here',
            authenticating: 'Logging in...',
            accessGranted: 'Access Granted',
            initializingSystem: 'Setting up your account...',
        },

        // Dashboard
        dashboard: {
            title: 'Home',
            greeting: 'Welcome back',
            yourLocation: 'Your Location',
            safetyStatus: 'Safety Status',
            weatherTitle: 'Weather',
            newsTitle: 'Latest Updates',
            localNews: 'Nearby',
            cityNews: 'Your City',
            noAlerts: 'No active alerts',
            youAreSafe: 'Everything looks good!',
            lowRisk: 'Low Risk',
            moderateRisk: 'Moderate Risk',
            highRisk: 'High Risk',
            wind: 'Wind',
            humidity: 'Humidity',
            temperature: 'Temperature',
            updatedAgo: 'Updated',
            viewOnMap: 'View on Map',
            newsItems: 'updates',
        },

        // Contacts
        contacts: {
            title: 'Contacts',
            network: 'My Network',
            addContact: 'Add Contact',
            searchContacts: 'Search contacts...',
            noContacts: 'No contacts yet',
            addFirstContact: 'Add your first contact',
            connected: 'connected',
            online: 'Online',
            offline: 'Offline',
            lastSeen: 'Last seen',
            sendMessage: 'Message',
            viewLocation: 'Location',
            checkStatus: 'Check Status',
            shareLink: 'Share Link',
            scanQR: 'Scan QR',
            enterID: 'Enter ID',
            nearby: 'Nearby',
            relationship: 'Relationship',
            family: 'Family',
            friend: 'Friend',
            colleague: 'Colleague',
            emergency: 'Emergency Contact',
            requestSent: 'Request Sent!',
            waitingResponse: 'Waiting for response...',
        },

        // Safety Status
        status: {
            safe: 'Safe',
            home: 'At Home',
            warning: 'Caution',
            danger: 'In Danger',
            sos: 'SOS',
            unknown: 'Unknown',
        },

        // Guardian AI
        guardian: {
            title: 'AI Guardian',
            subtitle: 'Your safety assistant',
            askQuestion: 'Ask me anything about safety...',
            thinking: 'Thinking...',
            voiceMode: 'Voice',
            textMode: 'Text',
            safetyTips: 'Safety Tips',
            emergencyAdvice: 'Emergency Advice',
        },

        // Alerts
        alerts: {
            title: 'Alerts',
            activeAlerts: 'Active Alerts',
            noActiveAlerts: 'No active alerts in your area',
            reportHazard: 'Report Hazard',
            verifyReport: 'Verify Report',
            viewDetails: 'View Details',
            distance: 'away',
            reportedBy: 'Reported by',
            verified: 'Verified',
            unverified: 'Unverified',
        },

        // Emergency
        emergency: {
            title: 'Emergency',
            callPolice: 'Police',
            callAmbulance: 'Ambulance',
            callFire: 'Fire Dept.',
            iAmSafe: 'I Am Safe',
            needHelp: 'I Need Help',
            sosActivated: 'SOS Activated',
            shareLocation: 'Share My Location',
        },

        // Profile
        profile: {
            title: 'Profile',
            myProfile: 'My Profile',
            editProfile: 'Edit Profile',
            changeAvatar: 'Change Avatar',
            locationSharing: 'Location Sharing',
            notifications: 'Notifications',
            language: 'Language',
            about: 'About',
            version: 'Version',
        },

        // Time
        time: {
            justNow: 'Just now',
            minutesAgo: 'min ago',
            hoursAgo: 'hours ago',
            today: 'Today',
            yesterday: 'Yesterday',
        },
    },

    ar: {
        // App General
        appName: 'لا ناجا',
        tagline: 'حارس أمان عائلتك',
        welcome: 'مرحباً',
        loading: 'جاري التحميل...',
        error: 'حدث خطأ ما',
        success: 'تم بنجاح!',
        cancel: 'إلغاء',
        confirm: 'تأكيد',
        save: 'حفظ',
        delete: 'حذف',
        edit: 'تعديل',
        close: 'إغلاق',
        back: 'رجوع',
        next: 'التالي',
        done: 'تم',
        search: 'بحث',
        filter: 'تصفية',
        refresh: 'تحديث',
        settings: 'الإعدادات',
        help: 'مساعدة',
        logout: 'تسجيل الخروج',

        // Navigation
        nav: {
            home: 'الرئيسية',
            map: 'الخريطة',
            contacts: 'جهات الاتصال',
            alerts: 'التنبيهات',
            profile: 'الملف الشخصي',
            guardian: 'الحارس',
        },

        // Auth
        auth: {
            login: 'تسجيل الدخول',
            register: 'إنشاء حساب',
            username: 'اسم المستخدم',
            password: 'كلمة المرور',
            confirmPassword: 'تأكيد كلمة المرور',
            fullName: 'الاسم الكامل',
            enterName: 'أدخل اسمك الكامل',
            chooseAvatar: 'اختر صورتك الرمزية',
            selectAvatar: 'اختر صورة تمثلك',
            createAccount: 'إنشاء الحساب',
            accessSystem: 'الدخول',
            forgotPassword: 'نسيت كلمة المرور؟',
            alreadyHaveAccount: 'لديك حساب بالفعل؟',
            dontHaveAccount: 'ليس لديك حساب؟',
            registerHere: 'سجل هنا',
            loginHere: 'ادخل هنا',
            authenticating: 'جاري الدخول...',
            accessGranted: 'تم الدخول بنجاح',
            initializingSystem: 'جاري إعداد حسابك...',
        },

        // Dashboard
        dashboard: {
            title: 'الرئيسية',
            greeting: 'أهلاً بعودتك',
            yourLocation: 'موقعك الحالي',
            safetyStatus: 'حالة الأمان',
            weatherTitle: 'الطقس',
            newsTitle: 'آخر المستجدات',
            localNews: 'بالقرب منك',
            cityNews: 'مدينتك',
            noAlerts: 'لا توجد تنبيهات',
            youAreSafe: 'كل شيء على ما يرام!',
            lowRisk: 'خطر منخفض',
            moderateRisk: 'خطر متوسط',
            highRisk: 'خطر عالي',
            wind: 'الرياح',
            humidity: 'الرطوبة',
            temperature: 'الحرارة',
            updatedAgo: 'آخر تحديث',
            viewOnMap: 'عرض على الخريطة',
            newsItems: 'تحديثات',
        },

        // Contacts
        contacts: {
            title: 'جهات الاتصال',
            network: 'شبكتي',
            addContact: 'إضافة جهة اتصال',
            searchContacts: 'ابحث في جهات الاتصال...',
            noContacts: 'لا توجد جهات اتصال',
            addFirstContact: 'أضف أول جهة اتصال',
            connected: 'متصل',
            online: 'متصل الآن',
            offline: 'غير متصل',
            lastSeen: 'آخر ظهور',
            sendMessage: 'رسالة',
            viewLocation: 'الموقع',
            checkStatus: 'تحقق من الحالة',
            shareLink: 'مشاركة الرابط',
            scanQR: 'مسح QR',
            enterID: 'إدخال المعرف',
            nearby: 'بالقرب',
            relationship: 'العلاقة',
            family: 'عائلة',
            friend: 'صديق',
            colleague: 'زميل',
            emergency: 'جهة طوارئ',
            requestSent: 'تم إرسال الطلب!',
            waitingResponse: 'في انتظار الرد...',
        },

        // Safety Status
        status: {
            safe: 'بأمان',
            home: 'في المنزل',
            warning: 'تحذير',
            danger: 'في خطر',
            sos: 'نجدة',
            unknown: 'غير معروف',
        },

        // Guardian AI
        guardian: {
            title: 'الحارس الذكي',
            subtitle: 'مساعدك للسلامة',
            askQuestion: 'اسألني أي شيء عن السلامة...',
            thinking: 'جاري التفكير...',
            voiceMode: 'صوت',
            textMode: 'نص',
            safetyTips: 'نصائح السلامة',
            emergencyAdvice: 'إرشادات الطوارئ',
        },

        // Alerts
        alerts: {
            title: 'التنبيهات',
            activeAlerts: 'التنبيهات النشطة',
            noActiveAlerts: 'لا توجد تنبيهات في منطقتك',
            reportHazard: 'الإبلاغ عن خطر',
            verifyReport: 'تأكيد البلاغ',
            viewDetails: 'عرض التفاصيل',
            distance: 'المسافة',
            reportedBy: 'أبلغ عنه',
            verified: 'موثق',
            unverified: 'غير موثق',
        },

        // Emergency
        emergency: {
            title: 'الطوارئ',
            callPolice: 'الشرطة',
            callAmbulance: 'الإسعاف',
            callFire: 'الإطفاء',
            iAmSafe: 'أنا بخير',
            needHelp: 'أحتاج مساعدة',
            sosActivated: 'تم تفعيل النجدة',
            shareLocation: 'مشاركة موقعي',
        },

        // Profile
        profile: {
            title: 'الملف الشخصي',
            myProfile: 'ملفي الشخصي',
            editProfile: 'تعديل الملف',
            changeAvatar: 'تغيير الصورة',
            locationSharing: 'مشاركة الموقع',
            notifications: 'الإشعارات',
            language: 'اللغة',
            about: 'حول التطبيق',
            version: 'الإصدار',
        },

        // Time
        time: {
            justNow: 'الآن',
            minutesAgo: 'دقيقة مضت',
            hoursAgo: 'ساعات مضت',
            today: 'اليوم',
            yesterday: 'أمس',
        },
    },
};

// Helper hook to get translations
export const getLocale = (lang: SupportedLanguage): Translations => {
    return LOCALES[lang] || LOCALES.en;
};

// RTL check
export const isRTL = (lang: SupportedLanguage): boolean => {
    return lang === 'ar';
};

// Format time ago in the current language
export const formatTimeAgo = (date: Date, lang: SupportedLanguage): string => {
    const t = LOCALES[lang].time;
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diff < 60) return t.justNow;
    if (diff < 3600) return `${Math.floor(diff / 60)} ${t.minutesAgo}`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} ${t.hoursAgo}`;

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return t.yesterday;

    return t.today;
};

export default LOCALES;
